'use client';

import { c as _c } from "react/compiler-runtime";
import type { PullRequestDemo } from '@/lib/code-sessions/pull-request-demo/demo-state';
import { ArrowUpRight, ChevronRight, CircleMinus, MoreHorizontal, RotateCcw, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useId } from 'react';
import { PrV2DemoPlayer } from './pr-v2-demo-player';
import { PR_V2_DEMO_CARD_ID, usePrV2DemoCollapsed } from './use-pr-v2-demo-collapsed';
import { SESSION_REPLAY_BLOCK_CLASS } from '@/components/providers/session-replay-masking';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Pill } from '@/components/ui/pill';
import { WorkingDots } from '@/components/ui/working-dots';
import { useRerecordPrV2Demo } from '@/data/v2/pr/use-pr-v2-demo';
import { useNow } from '@/hooks/use-now';
import { codeAttachmentUrl } from '@/lib/code-sessions/attachment-types';
import { isPullRequestDemoInProgress } from '@/lib/code-sessions/pull-request-demo/demo-state';
import { cn } from '@/lib/utils';
import { getFromNow } from '@/lib/utils/date-formatters';

// The page lowers the sidebar while this card shows, so its Reviewers heading lines up with the
// card's row (`pr-v2-page-client.tsx`): keep the row's height in step with that offset.
const CARD_CLASS = 'mb-3 max-w-[840px] rounded-lg border border-border';
const CARD_ROW_CLASS = 'flex items-center gap-3 px-3 py-1.5';
const STATUS_CLASS = 'flex shrink-0 items-center gap-1 text-xs text-zinc-500';

/** The pull request a demo card films, which a new take runs on. */
type PrV2DemoPullRequest = {
  owner: string;
  repo: string;
  number: string;
  /** The head commit the page shows, which a new take films unless a push lands first. */
  headSha: string | null;
};

/** Demos run only on /code installations, which are internal, like the Code tab's badge says. */
export function PrV2DemoInternalPill(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("shrink-0 px-1.5 py-0.5 text-[11px]", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <Pill text="Internal" tooltipText="Only enabled for internal installations" className={t1} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}

/** The card's name, the same in every state, so the row keeps its shape when the video arrives. */
function DemoTitle() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <><span className="shrink-0 text-sm font-medium text-foreground">Demo</span><PrV2DemoInternalPill /></>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Where the demo stands, as the card and the sidebar's demo row both say it: cubic recording or
 * updating it, or a session that filmed nothing. A take ready to watch shows `ready`.
 */
export function PrV2DemoProgress(t0) {
  const $ = _c(6);
  const {
    demo,
    ready
  } = t0;
  switch (demo.state) {
    case "recording":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <WorkingDots label="Recording" />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "ready":
      {
        let t1;
        if ($[1] !== demo.updating || $[2] !== ready) {
          t1 = demo.updating ? <WorkingDots label="Updating" /> : ready;
          $[1] = demo.updating;
          $[2] = ready;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "no_demo":
      {
        let t1;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <><CircleMinus className="size-3" aria-hidden="true" />Skipped</>;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "failed":
      {
        let t1;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <><X className="size-3 text-destructive" aria-hidden="true" />Failed</>;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
  }
}

/**
 * The card's actions: a new take on the pull request's latest commit, which waits for a take
 * cubic is still filming, and the demo's session, in a new tab so the pull request stays open.
 */
function DemoActions(t0) {
  const $ = _c(19);
  const {
    demo,
    pullRequest
  } = t0;
  const {
    owner,
    repo,
    number,
    headSha
  } = pullRequest;
  const rerecord = useRerecordPrV2Demo(owner, repo, number);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DropdownMenuTrigger asChild={true}><Button type="button" variant="ghost" size="tiny" aria-label="Demo actions" className="shrink-0 rounded-sm text-zinc-500 hover:text-zinc-300"><MoreHorizontal /></Button></DropdownMenuTrigger>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== demo || $[2] !== rerecord.isPending) {
    t2 = rerecord.isPending || isPullRequestDemoInProgress(demo);
    $[1] = demo;
    $[2] = rerecord.isPending;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== rerecord) {
    t3 = () => rerecord.mutate();
    $[4] = rerecord;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <RotateCcw className="size-3.5 text-zinc-400" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== headSha) {
    t5 = headSha ? <span className="ml-auto font-mono text-xs text-zinc-500">{headSha.slice(0, 7)}</span> : null;
    $[7] = headSha;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t5) {
    t6 = <DropdownMenuItem disabled={t2} onSelect={t3}>{t4}Re-record{t5}</DropdownMenuItem>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `/code/${demo.sessionId}`;
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowUpRight className="size-3.5 text-zinc-400" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7) {
    t9 = <DropdownMenuItem asChild={true}><a href={t7} target="_blank" rel="noopener noreferrer" aria-label="Open session (opens in a new tab)">{t8}Open session</a></DropdownMenuItem>;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t9) {
    t10 = <DropdownMenu modal={false}>{t1}<DropdownMenuContent align="end" className="min-w-[200px]">{t6}{t9}</DropdownMenuContent></DropdownMenu>;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}

/**
 * The demo cubic filmed of the pull request, above the description so the description's collapse
 * never hides it: a row saying where the demo stands while it has no video, then the video. Its
 * menu asks for a new take on the pull request's latest commit. The sidebar's demo row carries
 * the same states.
 */
export function PrV2DemoCard(t0) {
  const $ = _c(45);
  const {
    demo,
    pullRequest
  } = t0;
  const [isCollapsed, setIsCollapsed] = usePrV2DemoCollapsed();
  const videoRegionId = useId();
  const now = useNow();
  const video = demo.state === "ready" ? demo.video : null;
  let t1;
  if ($[0] !== now || $[1] !== video) {
    t1 = video ? getFromNow(video.recordedAt, {
      now
    }) : null;
    $[0] = now;
    $[1] = video;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const age = t1;
  let t2;
  if ($[3] !== age || $[4] !== video) {
    t2 = video && <time dateTime={video.recordedAt} title={`Recorded ${age}`}>{age}</time>;
    $[3] = age;
    $[4] = video;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== demo || $[7] !== t2) {
    t3 = <span className={STATUS_CLASS}><PrV2DemoProgress demo={demo} ready={t2} /></span>;
    $[6] = demo;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const status = t3;
  let t4;
  if ($[9] !== demo || $[10] !== pullRequest) {
    t4 = <DemoActions demo={demo} pullRequest={pullRequest} />;
    $[9] = demo;
    $[10] = pullRequest;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const actions = t4;
  if (!video) {
    let t5;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn(CARD_CLASS, CARD_ROW_CLASS);
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex min-w-0 flex-1 items-center gap-2"><ChevronRight aria-hidden="true" className="size-4 shrink-0 text-zinc-500 opacity-40" /><DemoTitle /></div>;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== actions || $[15] !== status) {
      t7 = <div id={PR_V2_DEMO_CARD_ID} className={t5}>{t6}{status}{actions}</div>;
      $[14] = actions;
      $[15] = status;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    return t7;
  }
  let t5;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn(CARD_CLASS, "overflow-hidden", SESSION_REPLAY_BLOCK_CLASS);
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== isCollapsed || $[19] !== setIsCollapsed) {
    t6 = () => setIsCollapsed(!isCollapsed);
    $[18] = isCollapsed;
    $[19] = setIsCollapsed;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const t7 = !isCollapsed;
  const t8 = !isCollapsed && "rotate-90";
  let t9;
  if ($[21] !== t8) {
    t9 = cn("size-4 shrink-0 text-zinc-500 transition-transform group-hover/demo:text-zinc-300", t8);
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t9) {
    t10 = <ChevronRight aria-hidden="true" className={t9} />;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <DemoTitle />;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t6 || $[28] !== t7 || $[29] !== videoRegionId) {
    t12 = <button type="button" onClick={t6} aria-expanded={t7} aria-controls={videoRegionId} className="group/demo flex min-w-0 flex-1 items-center gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{t10}{t11}</button>;
    $[26] = t10;
    $[27] = t6;
    $[28] = t7;
    $[29] = videoRegionId;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== actions || $[32] !== status || $[33] !== t12) {
    t13 = <div className={CARD_ROW_CLASS}>{t12}{status}{actions}</div>;
    $[31] = actions;
    $[32] = status;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== demo.sessionId || $[36] !== isCollapsed || $[37] !== video) {
    t14 = isCollapsed ? null : <PrV2DemoPlayer key={video.id} url={codeAttachmentUrl(demo.sessionId, video.id)} caption={video.caption} />;
    $[35] = demo.sessionId;
    $[36] = isCollapsed;
    $[37] = video;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14 || $[40] !== videoRegionId) {
    t15 = <div id={videoRegionId}>{t14}</div>;
    $[39] = t14;
    $[40] = videoRegionId;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t13 || $[43] !== t15) {
    t16 = <section id={PR_V2_DEMO_CARD_ID} aria-label="Demo video" className={t5}>{t13}{t15}</section>;
    $[42] = t13;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  return t16;
}